/**
 * Number → lowercase roman numeral (i, ii, iii, iv, ...).
 *
 * Handles integers 1–3999 — the standard subtractive range. Inputs
 * above 3999 (which would emit four or more leading `m`s and stop
 * being conventional) and inputs below 1 return the empty string.
 * For folio / footnote / chapter-mark use cases on the site — pair
 * with the `.cs-roman` typography utility for the manuscript register.
 */
const ROMAN: Array<readonly [number, string]> = [
  [1000, "m"],
  [900, "cm"],
  [500, "d"],
  [400, "cd"],
  [100, "c"],
  [90, "xc"],
  [50, "l"],
  [40, "xl"],
  [10, "x"],
  [9, "ix"],
  [5, "v"],
  [4, "iv"],
  [1, "i"],
];

// `Number.prototype.toLocaleString("en-CA")` is non-deterministic across
// runtimes — Node's bundled ICU and Chromium's V8 disagree on whether
// thousands are grouped with `,` (U+002C) or NNBSP (U+202F). When the
// formatted string lands in JSX text, the server-rendered HTML and the
// client-rendered HTML differ by exactly those bytes and React aborts
// hydration. Use this helper for any integer that renders to the DOM
// during SSR.
export function fmtIntCA(n: number): string {
  if (!Number.isFinite(n)) return "";
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(Math.trunc(n));
  return sign + abs.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function toRoman(n: number): string {
  if (!Number.isFinite(n)) return "";
  const v0 = Math.floor(n);
  if (v0 < 1 || v0 > 3999) return "";
  let out = "";
  let v = v0;
  for (const [val, sym] of ROMAN) {
    while (v >= val) {
      out += sym;
      v -= val;
    }
  }
  return out;
}
